<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div class="header-title">
            Location Information
            <span v-if="personLocations.length === 1">
              ({{ personLocations[0].personTypeName }})
            </span>
          </div>
        </template>
      </es-header>
    </div>
    <es-loader v-if="loading" style="margin: 2rem" />
    <es-exception
      v-else-if="loadingError"
      style="padding: 3rem; margin: 0"
      exceptionMessage="There was an issue.
      Please try again or contact your administrator."
    />
    <div v-else class="content">
      <div class="table-wrapper">
        <table class="table">
          <es-sorter
            :columnHeaders="filteredColumnHeaders"
            @updateSorter="updateSorter"
          />
          <transaction-profile-location-info-row
            @showDetails="showingDetails($event)"
            v-for="(personLocation, index) in personLocations"
            :key="index"
            :personLocation="personLocation"
            :currentPersonTypeCategory="personLocations[0].personTypeCategory"
          />
      </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSorter from '@/components/EsSorter.vue';
import TransactionProfileLocationInfoRow from '@/components/TransactionProfileLocationInfoRow.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';

library.add(faPlus);

export default {
  mixins: [FieldLabels, PermissionManager],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsSorter,
    TransactionProfileLocationInfoRow,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    personLocations: {
      type: Array,
      required: true,
    },
    userHasAccessToPersonLocation: {
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      myColumnHeaders: [],
    };
  },
  created() {
    this.onLoad();
  },
  watch: {
    personLocations: {
      handler(newValue) {
        if (newValue && newValue.length) {
          this.initializeColumnHeaders();
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('propertiesModule', ['properties']),
    ...mapGetters('lookups', ['activeGrades', 'activeSources', 'activeTitles']),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
      'getFieldLabelByPersonType',
    ]),
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    filteredColumnHeaders() {
      return this.myColumnHeaders.filter(item => item.show);
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGrades',
      'loadPersonTypes',
      'loadSources',
      'loadTitles',
    ]),
    async onLoad() {
      try {
        this.loading = true;
        await Promise.all([
          this.loadGrades(),
          this.loadPersonTypes(),
          this.loadSources(),
          this.loadTitles(),
        ]);
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    initializeColumnHeaders() {
      const personTypeCategory = this.personLocations.length > 0 ? this.personLocations[0].personTypeCategory : null;
      this.myColumnHeaders = [
        {
          field: null,
          header: 'Update Date',
          show: true,
          sortable: true,
          style: { width: '15%' },
          title: 'Update Date',
        },
        {
          field: null,
          header: this.fieldLabel('SCHOOL_YEAR'),
          show: this.fieldEnabled('SCHOOL_YEAR'),
          sortable: true,
          style: { width: '15%' },
          title: 'School Year',
        },
        {
          field: null,
          header: 'Location Info',
          show: true,
          sortable: true,
          style: { width: '30%' },
          title: 'Location Info',
        },
        {
          field: null,
          header: this.fieldLabel('LOCAL_STUDENT_ID'),
          show: this.fieldEnabled('LOCAL_STUDENT_ID'),
          sortable: true,
          style: { width: '10%' },
          title: 'ID Info',
        },
        {
          field: null,
          header: this.fieldLabel('ALT_STUDENT_ID'),
          show: this.fieldEnabled('ALT_STUDENT_ID'),
          sortable: true,
          style: { width: '10%' },
          title: 'Alternate ID',
        },
        {
          field: null,
          header: this.fieldLabel('HIRE_DATE'),
          show: personTypeCategory === 'EMPLOYEE' && this.fieldEnabled('HIRE_DATE'),
          sortable: true,
          style: { width: '10%' },
          title: 'Hire Date',
        },
        {
          field: null,
          header: this.fieldLabel('JOB_CLASS_CODE'),
          show: personTypeCategory === 'EMPLOYEE' && this.fieldEnabled('JOB_CLASS_CODE'),
          sortable: true,
          style: { width: '10%' },
          title: 'Job Classification',
        },
        {
          field: null,
          header: this.fieldLabel('SALARY'),
          show: personTypeCategory === 'EMPLOYEE' && this.fieldEnabled('SALARY'),
          sortable: true,
          style: { width: '10%' },
          title: 'Annual Salary',
        },
        {
          field: null,
          header: this.fieldLabel('GRADE_CODE'),
          show: personTypeCategory === 'STUDENT' && this.fieldEnabled('GRADE_CODE'),
          sortable: true,
          style: { width: '10%' },
          title: 'Grade',
        },
        {
          field: null,
          header: this.fieldLabel('ACTIVE'),
          show: this.fieldEnabled('ACTIVE'),
          sortable: true,
          style: { width: '5%' },
          title: personTypeCategory === 'STUDENT' ? 'Active/Inactive Indicator' : 'Status',
        },
        {
          field: null,
          header: 'Actions',
          show: true,
          sortable: false,
          style: { width: '5%' },
          title: 'Actions',
        },
      ];
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.person.personType);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.person.personType);
    },
    showingDetails(object) {
      this.$emit('showDetails', object);
    },
    updateSorter() {
      this.$emit('updateSorter');
    },
  },
};
</script>

<style lang="sass" scoped>
  .list-content
    padding-right: 0.5rem
  .table-wrapper
    overflow-x: auto
    white-space: nowrap
</style>
